import { FieldArray, Form, Formik, FormikProps } from 'formik';
import { isEmpty } from 'lodash';
import { FC, Fragment, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { CREATE_DATA_SOURCE_CRM } from '../constants';
import { useAppSelector } from '@/reducers';
import { CrmParameter, CrmParameters } from '../../../../types';
import { useWorkspace } from '@/features/workspaces/hooks';
import { stashFieldDefinitions, stashParameters } from '@/features/cyclr/dataSourceIntegrationSlice';
import { DataSource, DataSourceCrmConfig } from '@/models/dataSource';
import { StepPageActions } from '@/global/components';
import { stashDataSource } from '@/features/dataSources/dataSourcesSlice';
import { useUpdateCrmDataSourceParametersMutation } from '@/services/dataSources';

type CrmDataSourceParametersProps = {
  onNextStep?: VoidFunction;
  onPrevStep?: VoidFunction;
  onGoToStep?: (id: CREATE_DATA_SOURCE_CRM) => void;
  onCancel?: VoidFunction;
};

export const CrmDataSourceParametersStep: FC<CrmDataSourceParametersProps> = ({ onNextStep, onPrevStep, onGoToStep }) => {
  const dispatch = useDispatch();
  const workspace = useWorkspace();

  const crmParametersFormRef = useRef<FormikProps<CrmParameters>>(null);

  const dataSource = useAppSelector((state) => state.dataSources.edit?.dataSource) as Partial<DataSource<DataSourceCrmConfig>> | undefined;
  const paramDefs = useAppSelector((state) => state.dataSourceIntegration.parameterDefinitions);
  const storedParams = useAppSelector((state) => state.dataSourceIntegration.parameters);
  const storedParamMap = new Map<number, CrmParameter>(storedParams.map((param) => [param.parameterId, param]));

  const params: CrmParameter[] = paramDefs.map((paramDef) =>
    storedParamMap.has(paramDef.parameterId)
      ? {
          ...(storedParamMap.get(paramDef.parameterId) as CrmParameter),
        }
      : {
          ...paramDef,
          value: null,
        }
  );

  const [updateParameters, { data: updateParametersResponse, isLoading: isUpdateParametersLoading }] = useUpdateCrmDataSourceParametersMutation();

  useEffect(() => {
    if (updateParametersResponse) {
      dispatch(stashDataSource(updateParametersResponse.dataSource));

      if (updateParametersResponse.integrationsResult.cyclr.action.fields?.length) {
        dispatch(stashFieldDefinitions(updateParametersResponse.integrationsResult.cyclr.action.fields));
        onNextStep();
      } else {
        onGoToStep(CREATE_DATA_SOURCE_CRM.CHECK_INSTALLATION);
      }
    }
  }, [updateParametersResponse]);

  const handleSubmit = () => {
    console.debug('On parameters submit');
  };

  const submitParameters = (values: CrmParameters) => {
    if (dataSource && dataSource.id) {
      updateParameters({
        workspaceId: workspace.id,
        dataSourceId: dataSource.id,
        parameters: values.parameters
          .filter((param) => param.value !== undefined && param.value !== null)
          .map((param) => ({
            stepIds: param.stepIds,
            parameterId: param.parameterId,
            mappingType: param.mappingType,
            name: param.name,
            value: param.value as string,
          })),
      });
    }
  };

  const handleNextStep = async () => {
    await crmParametersFormRef.current?.submitForm();

    const values = crmParametersFormRef.current?.values;
    if (crmParametersFormRef.current?.isValid && values) {
      dispatch(stashParameters(values.parameters.filter((param) => param.value !== undefined && param.value !== null)));
      submitParameters(values);
    }
  };

  return (
    <>
      <Formik innerRef={crmParametersFormRef} initialValues={{ parameters: params }} validateOnBlur={false} validateOnChange={false} onSubmit={handleSubmit}>
        {({ values, setFieldValue }) => {
          return (
            <Form>
              <FieldArray
                name='parameters'
                validateOnChange={false}
                render={(clickEventHelper) => (
                  <Fragment>
                    {clickEventHelper &&
                      !isEmpty(clickEventHelper) &&
                      values.parameters.map((param, index) => (
                        <div
                          key={param.parameterId}
                          className='mt-6 h-auto rounded-lg border border-solid border-gray-200 bg-gray-50 px-6 py-4 text-left font-medium'
                        >
                          <h3 className='mb-3'>{param.description}</h3>
                          {param.lookupValues.map((lookupValue) => (
                            <div key={lookupValue.Value} className={'flex items-center py-1'}>
                              <label>
                                <input
                                  type={'radio'}
                                  className={'mr-3'}
                                  name={param.parameterId + ''}
                                  value={lookupValue.Value}
                                  checked={param.value === lookupValue.Value}
                                  onChange={() => setFieldValue(`parameters.${index}`, { ...param, name: lookupValue.Name, value: lookupValue.Value })}
                                />
                                {lookupValue.Name}
                              </label>
                            </div>
                          ))}
                        </div>
                      ))}
                  </Fragment>
                )}
              />
            </Form>
          );
        }}
      </Formik>

      <StepPageActions
        primaryButton={{
          text: 'COMMON.confirm',
          onClick: handleNextStep,
          isLoading: isUpdateParametersLoading,
        }}
        secondaryButton={{
          text: 'COMMON.cancel',
          onClick: onPrevStep,
        }}
      />
    </>
  );
};
